import React, { useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';
import Hls from 'hls.js';
import PlayTimeStampSyncController from './PlayTimeStampSyncController';
import { DFVVideo, DFVWrap } from '../DynamicFieldView.style';
import VideoEventBus from '../utils/VideoEventBus';
import useIntersectionObserver from '@react-hook/intersection-observer';
import AutoPlayError from '../components/AutoPlayError';

// Placeholder for now, will address in follow-up spike.
const needsGammaAdjust = false;

let supportsNativeHLS = false;
const options = {
    threshold: 0.3,
    initialIsIntersecting: false
};
const STREAM_PAUSE_TIME = 30000;

if (typeof document !== 'undefined') {
    let video = document && document.createElement('video');
    supportsNativeHLS = Boolean(video?.canPlayType('application/vnd.apple.mpegURL'));
    video = null;
}

const supportsHLSJS = Hls.isSupported();
export const canUseNativeHLS = () => {
    return supportsNativeHLS;
};

export const canPaintToCanvas = () => {
    return false;
    // return supportsHLSJS && !supportsNativeHLS;
};

const DFVPlayer = forwardRef(({
    stream,
    setStreamError,
    setIsLoading,
    setAutoPlayError,
    autoPlayError,
    frameDrawer,
    hidden = false,
    EventBus = VideoEventBus,
    playEndTime,
    syncMode = 'playback'
}, parentRef) => {
    const videoRef = useRef(null);
    const syncController = useRef(null);
    const videoWrapRef = useRef(null);
    const { isIntersecting } = useIntersectionObserver(videoWrapRef, options);

    useImperativeHandle(parentRef, () => videoRef.current, []);

    useEffect(() => {
        let video = videoRef.current;
        let eventBus = null;
        if (video && video.parentNode) {
            eventBus = new EventBus({ video, stream, syncController, setStreamError, setIsLoading, frameDrawer, setAutoPlayError });
            eventBus.bind();

            const initEvent = new Event('init');
            initEvent.data = video;
            if (supportsHLSJS) {
                video.dispatchEvent(initEvent);
            } else if (supportsNativeHLS) {
                video.src = stream;
            } else {
                throw new Error(`DFV Playback unsupported: supportsNativeHLS: ${supportsNativeHLS}, supportsHLSJS: ${supportsHLSJS}`);
            }
        }

        return () => {
            if (eventBus) {
                eventBus.destroy();
                eventBus = null;
            }
            if (video) {
                video = eventBus = null;
            }
            if (syncController.current) {
                syncController.current.destroy();
            }
        };
    }, [stream]);

    useEffect(() => {
        let timeoutId;
        const video = videoRef.current;
        if (!video) {
            return;
        }

        if (!isIntersecting) {
            timeoutId = setTimeout(() => {
                if (supportsHLSJS) {
                    const stopEvent = new Event('stop');
                    stopEvent.data = video;
                    video.dispatchEvent(stopEvent);
                } else if (supportsNativeHLS) {
                    video.removeAttribute('src');
                    video.load();
                }
            }, STREAM_PAUSE_TIME);
        } else {
            clearTimeout(timeoutId);
            timeoutId = null;
            if (supportsHLSJS) {
                const startEvent = new Event('start');
                startEvent.data = video;
                video.dispatchEvent(startEvent);
            } else if (supportsNativeHLS) {
                if (video.src !== stream) {
                    video.src = stream;
                    video.load();
                }
                video.play();
            }
        }
        return () => clearTimeout(timeoutId);
    }, [isIntersecting]);

    return (
        <DFVWrap $isCanvas={canPaintToCanvas()} ref={videoWrapRef}>
            <PlayTimeStampSyncController ref={syncController} player={videoRef} playEndTime={playEndTime} syncMode={syncMode} />
            <DFVVideo
                ref={videoRef}
                autoPlay={false}
                muted={true}
                preload='metadata'
                type='application/x-mpegURL'
                loop={false}
                playsInline={true}
                /* FF will still show these despite `controls` being false
                    so turn them off explicitly.
                */
                controlsList='nodownload nofullscreen noremoteplayback'
                disablePictureInPicture={true}
                disableRemotePlayback={true}
                xWebkitAirplay='deny'
                crossOrigin='anonymous'
                hidden={hidden}
                $needsGammaAdjust={needsGammaAdjust}
            />
            {autoPlayError && <AutoPlayError videoRef={videoRef} />}
        </DFVWrap>
    );
});

DFVPlayer.displayName = 'DFVPlayer';

DFVPlayer.propTypes = {
    EventBus: function (props, propName, componentName) {
        const prop = props[propName];
        if (prop && !Object.prototype.isPrototypeOf.call(VideoEventBus, prop)) {
            return new Error(`Invalid prop ${propName}.  ${propName} must extend VideoEventBus`);
        }
    },
    frameDrawer: PropTypes.shape({
        current: PropTypes.object
    }),
    hidden: PropTypes.bool,
    stream: PropTypes.string,
    setStreamError: PropTypes.func,
    setIsLoading: PropTypes.func,
    setAutoPlayError: PropTypes.func,
    autoPlayError: PropTypes.bool,
    playEndTime: PropTypes.string,
    syncMode: PropTypes.string

};

export default DFVPlayer;
